import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { workspaceApi, pipelineApi, projectApi, taskApi } from '@/lib/api'
import { useToast } from '@/stores/toast-store'
import { usePaletteStore } from '@/stores/palette-store'
import type { TaskPriority } from '@/types/task'

const PRIORITY_OPTIONS: { value: TaskPriority; label: string }[] = [
  { value: 'low', label: 'Rendah' },
  { value: 'medium', label: 'Sedang' },
  { value: 'high', label: 'Tinggi' },
  { value: 'urgent', label: 'Mendesak' },
]

interface QuickTaskFormProps {
  initialTitle: string
}

export function QuickTaskForm({ initialTitle }: QuickTaskFormProps) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const setOpen = usePaletteStore((s) => s.setOpen)

  const [title, setTitle] = useState(initialTitle)
  const [slug, setSlug] = useState('')
  const [projectId, setProjectId] = useState('')
  const [statusId, setStatusId] = useState('')
  const [priority, setPriority] = useState<TaskPriority>('medium')
  const [dueDate, setDueDate] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: workspaces } = useQuery({
    queryKey: ['workspaces'],
    queryFn: workspaceApi.getWorkspaces,
  })

  const { data: projects } = useQuery({
    queryKey: ['workspace', slug, 'projects'],
    queryFn: () => projectApi.getProjects(slug),
    enabled: !!slug,
  })

  const { data: statuses } = useQuery({
    queryKey: ['workspace', slug, 'pipeline'],
    queryFn: () => pipelineApi.getStatuses(slug),
    enabled: !!slug,
  })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!slug || !projectId || !title.trim()) return
    setError(null)
    setSubmitting(true)
    try {
      await taskApi.createTask(slug, {
        project_id: projectId,
        title: title.trim(),
        status_id: statusId || undefined,
        priority,
        due_date: dueDate || undefined,
      })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['workspace', slug, 'project', projectId, 'tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'today'] }),
      ])
      toast({ title: 'Tugas berhasil dibuat' })
      setOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal membuat tugas.')
    } finally {
      setSubmitting(false)
    }
  }

  const inputClass =
    'w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring'

  return (
    <form onSubmit={handleSubmit} className="space-y-3 border-t border-border pt-3">
      {/* Title */}
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Judul *</label>
        <input
          autoFocus
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={inputClass}
          placeholder="Nama tugas..."
        />
      </div>

      {/* Workspace */}
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Workspace *</label>
        <select
          required
          value={slug}
          onChange={(e) => {
            setSlug(e.target.value)
            setProjectId('')
            setStatusId('')
          }}
          className={inputClass}
        >
          <option value="">Pilih workspace...</option>
          {workspaces?.map((ws) => (
            <option key={ws.slug} value={ws.slug}>
              {ws.name}
            </option>
          ))}
        </select>
      </div>

      {/* Project */}
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Project *</label>
        <select
          required
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          disabled={!slug}
          className={inputClass}
        >
          <option value="">Pilih project...</option>
          {projects?.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {/* Status */}
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Status</label>
        <select
          value={statusId}
          onChange={(e) => setStatusId(e.target.value)}
          disabled={!slug}
          className={inputClass}
        >
          <option value="">Tidak ada</option>
          {statuses?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {/* Priority + Due date (side by side) */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Prioritas</label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as TaskPriority)}
            className={inputClass}
          >
            {PRIORITY_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Jatuh Tempo</label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      {error && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>
      )}

      <button
        type="submit"
        disabled={submitting || !slug || !projectId || !title.trim()}
        className="w-full rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
      >
        {submitting ? 'Menyimpan...' : 'Buat Tugas'}
      </button>
    </form>
  )
}
